import { a as handleClassAttribute, i as handleChildren, r as handleAnyAttribute, s as handleStyleAttribute, t as childToNode } from "./utils-C8z7KYTJ.js";
import { effect, isComputed, isSignal } from "alien-deepsignals";

//#region src/alien-deepsignals/utils.ts
function handleAnySignalAttribute(element, key, value) {
	if (isSignal(value) || isComputed(value)) {
		effect(() => handleAnyAttribute(element, key, value.get()));
		return;
	}
	handleAnyAttribute(element, key, value);
}
function handleClassSignalAttribute(element, value) {
	if (typeof value === "string") {
		handleClassAttribute(element, value);
		return;
	}
	if (Array.isArray(value)) {
		element.classList = "";
		element.classList.add(...value);
		return;
	}
	if (isSignal(value) || isComputed(value)) {
		effect(() => {
			element.classList = "";
			handleClassAttribute(element, value.get());
		});
		return;
	}
	for (const k of Object.keys(value)) effect(() => {
		let v = value[k];
		if (isSignal(v) || isComputed(v)) v = v.get();
		element.classList.toggle(k, v === true);
	});
}
function handleStyleSignalAttribute(element, value) {
	if (typeof value === "string") {
		handleStyleAttribute(element, value);
		return;
	}
	if (isSignal(value) || isComputed(value)) {
		effect(() => handleStyleAttribute(element, value.get()));
		return;
	}
	for (const key of Object.keys(value)) effect(() => {
		let val = value[key];
		if (isSignal(val) || isComputed(val)) val = val.get();
		element.style[key] = val;
	});
}
function handleDataSignalAttribute(element, value) {
	for (const k of Object.keys(value)) effect(() => {
		let v = value[k];
		if (isSignal(v) || isComputed(v)) v = v.get();
		if (typeof v === "string") element.dataset[k] = v;
	});
}
function handleSignalChildren(element, children) {
	if (isSignal(children) || isComputed(children)) {
		let placeholder = null;
		const getPlaceholder = () => {
			if (!placeholder) placeholder = document.createComment("signal placeholder");
			return placeholder;
		};
		const nodes = [];
		effect(() => {
			const newNodes = reactiveChildrenToNodes(children);
			if (newNodes.length === 0) newNodes.push(getPlaceholder());
			let lastInsertedNode = null;
			for (let i = 0; i < Math.max(newNodes.length, nodes.length); i++) {
				const newNode = newNodes[i];
				const node = nodes[i];
				const isInNew = node && newNodes.includes(node);
				if (node && !isInNew && i + 1 < nodes.length) {
					if (element.contains(node)) element.removeChild(node);
					nodes.splice(i, 1);
					i--;
					continue;
				}
				if (newNode && newNode === node) {
					lastInsertedNode = newNode;
					continue;
				}
				if (newNode) {
					if (node && element.contains(node)) element.insertBefore(newNode, node);
					else if (lastInsertedNode && element.contains(lastInsertedNode)) insertAfter(element, newNode, lastInsertedNode);
					else element.appendChild(newNode);
					const indexInOld = nodes.indexOf(newNode);
					if (indexInOld !== -1) nodes.splice(indexInOld, 1);
					nodes.splice(i, 0, newNode);
					lastInsertedNode = newNode;
					continue;
				}
				if (!newNode && node) {
					for (let j = i; j < nodes.length; j++) if (element.contains(nodes[j])) element.removeChild(nodes[j]);
					nodes.splice(i);
					break;
				}
			}
		});
	} else return handleChildren(element, children);
}
function reactiveChildrenToNodes(reactiveChild) {
	const value = getReactiveChildValue(reactiveChild);
	const nodes = [];
	if (Array.isArray(value)) value.forEach((v) => {
		const node = childToNode(v);
		if (node) nodes.push(node);
	});
	else {
		const node = childToNode(value);
		if (node) nodes.push(node);
	}
	return nodes;
}
function getReactiveChildValue(child) {
	return child.get();
}
function insertAfter(parent, newNode, referenceNode) {
	const nextNode = referenceNode.nextSibling;
	if (nextNode) parent.insertBefore(newNode, nextNode);
	else parent.appendChild(newNode);
}

//#endregion
//#region src/alien-deepsignals/index.ts
/**
* Creates a DOM element with [alien-deepsignals](https://www.npmjs.com/package/alien-deepsignals) aware attributes, styles, datasets, and children.
* @param tag Element tag name including SVG/MathML prefixes.
* @param attributes Optional attribute bag that can contain reactive `class`, `style`, `data`, and `children` props.
* @param children Additional children appended after `attributes.children`.
*/
function createElement(tag, attributes, ...children) {
	let element;
	if (tag === "svg" || tag.startsWith("svg:")) element = document.createElementNS("http://www.w3.org/2000/svg", tag === "svg" ? "svg" : tag.substring(4));
	else if (tag === "math" || tag.startsWith("math:")) element = document.createElementNS("http://www.w3.org/1998/Math/MathML", tag === "math" ? "math" : tag.substring(5));
	else element = document.createElement(tag);
	if (attributes) for (const name of Reflect.ownKeys(attributes)) {
		if (name === "children") {
			if (Array.isArray(attributes.children)) attributes.children.forEach((child) => handleSignalChildren(element, child));
			else handleSignalChildren(element, attributes.children);
			continue;
		}
		if (name === "class" && attributes.class) {
			handleClassSignalAttribute(element, attributes.class);
			continue;
		}
		if (name === "style" && attributes.style) {
			handleStyleSignalAttribute(element, attributes.style);
			continue;
		}
		if (name === "data" && attributes.data) {
			handleDataSignalAttribute(element, attributes.data);
			continue;
		}
		handleAnySignalAttribute(element, name, attributes[name]);
	}
	children.forEach((child) => handleSignalChildren(element, child));
	return element;
}
/**
* Shorthand alias for {@link createElement} to align with JSX/hyperscript expectations.
*/
const h = createElement;

//#endregion
export { h as n, reactiveChildrenToNodes as r, createElement as t };
//# sourceMappingURL=alien-deepsignals-Ck9PVJnS.js.map